// src/pages/BinaryTreeNeoFixed.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import Logo from "../assets/sun.png";

const DEPTH_OPTIONS = [2, 3, 4, 5, 6, 7, 8, 9, 10];

export default function BinaryTreeNeoFixed() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [depth, setDepth] = useState(3);
  const [customDepth, setCustomDepth] = useState("3");
  const canvasRef = useRef(null);

  const token = useMemo(
    () => localStorage.getItem("token") || localStorage.getItem("authToken") || "",
    []
  );

  const fetchTree = async (d = depth) => {
    setErr("");
    setLoading(true);

    if (!token) {
      setErr("Token not found. Please login first.");
      setLoading(false);
      return;
    }

    try {
      // Try axiosInstance first, fall back to direct fetch with full URL
      let resData = null;

      try {
        const res = await axiosInstance.get(`/api/binary/tree`, {
          params: { depth: d },
          headers: { Authorization: `Bearer ${token}` },
        });
        resData = res.data;
      } catch {
        // Fallback: direct API call
        const res = await fetch(
          `https://api.mysun.in/api/binary/tree?depth=${d}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          throw new Error(
            errBody?.message || errBody?.error || `HTTP ${res.status}`
          );
        }
        resData = await res.json();
      }

      setData(resData);

      const root = resData?.tree;
      const rid =
        root?.userID ?? root?.userId ?? root?.id ?? root?.userPkId ?? null;
      if (rid != null) setSelectedId(rid);
    } catch (e) {
      setErr(
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Failed to load tree"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTree(depth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const root = data?.tree || null;

  const zoomIn = () =>
    setZoom((z) => Math.min(2.0, Math.round((z + 0.1) * 10) / 10));
  const zoomOut = () =>
    setZoom((z) => Math.max(0.4, Math.round((z - 0.1) * 10) / 10));
  const zoomReset = () => setZoom(1);

  const centerCanvas = () => {
    const el = canvasRef.current;
    if (!el) return;
    el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
    el.scrollTop = 0;
  };

  useEffect(() => {
    if (!loading) setTimeout(centerCanvas, 60);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data, zoom]);

  const handleDepthChange = (newDepth) => {
    setDepth(newDepth);
    fetchTree(newDepth);
  };

  return (
    <div className="neo-page">
      <style>{CSS}</style>

      {err ? (
        <div className="neo-error">
          <b>Error:</b> {err}
        </div>
      ) : null}

      <div className="neo-top">
        <div className="neo-zoom">
          {/* Zoom controls */}
          <button className="neo-ic" onClick={zoomOut} title="Zoom out">−</button>
          <div className="neo-zoomv">{Math.round(zoom * 100)}%</div>
          <button className="neo-ic" onClick={zoomIn} title="Zoom in">+</button>
          <button className="neo-ic ghost" onClick={zoomReset} title="Reset zoom">R</button>
          <button className="neo-ic ghost" onClick={centerCanvas} title="Center tree">C</button>

          {/* Depth selector */}
          <div className="neo-custom-depth">
            <input
              type="number"
              min="1"
              max="100"
              value={customDepth}
              onChange={(e) => setCustomDepth(e.target.value)}
              className="neo-depth-input"
              placeholder="Depth"
            />
            <button
              className="neo-go-btn"
              onClick={() => {
                const d = parseInt(customDepth);
                if (d > 0 && d <= 100) {
                  handleDepthChange(d);
                } else {
                  alert("Please enter a depth between 1 and 100");
                }
              }}
              disabled={loading}
            >
              Go
            </button>
          </div>

          <button
            className="neo-btn"
            onClick={() => fetchTree(depth)}
            disabled={loading}
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="neo-canvas" ref={canvasRef}>
        {loading ? (
          <div className="neo-loading">
            <div className="neo-spinner" />
            <div className="neo-loading-text">Loading tree (depth {depth})…</div>
          </div>
        ) : null}

        {!loading && !root ? (
          <div className="neo-empty">No tree data found.</div>
        ) : null}

        {root ? (
          <div className="neo-stage" style={{ transform: `scale(${zoom})` }}>
            <ul className="neo-tree">
              <NeoNode
                node={root}
                selectedId={selectedId}
                onSelect={setSelectedId}
                logoSrc={Logo}
              />
            </ul>
          </div>
        ) : null}
      </div>

      <div className="neo-footer">© Sun entrepreneur — Binary Tree (depth: {depth})</div>
    </div>
  );
}

/** -------- helpers -------- **/
function getUserType(node) {
  const raw =
    node?.userType ??
    node?.usertype ??
    node?.type ??
    node?.roleType ??
    node?.memberType ??
    "";
  return String(raw || "").trim().toUpperCase();
}

/** -------- NODE -------- **/
function NeoNode({ node, selectedId, onSelect, logoSrc }) {
  const left = node?.left || null;
  const right = node?.right || null;
  const hasChildren = !!left || !!right;

  const uid = node?.userID ?? node?.userId ?? node?.id ?? node?.userPkId ?? "—";
  const name = node?.name ?? "—";
  const userType = getUserType(node);
  const isSelected = String(selectedId) === String(uid);
  const isEntrepreneur = userType === "ENTREPRENEUR";
  const typeClass = isEntrepreneur ? "type-ent" : "type-red";

  return (
    <li className="neo-li">
      <button
        type="button"
        className={["neo-card", isEntrepreneur ? "card-bg-ent" : "card-bg-red", isSelected ? "sel" : ""].join(" ")}
        onClick={() => onSelect(uid)}
      >
        <div className="neo-name">{name}</div>
        <img
          className="neo-logo"
          src={logoSrc}
          alt="logo"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
        <div className="neo-id">{uid}</div>
        <div className={"neo-chip " + typeClass}>{userType || "—"}</div>

        {/* TOOLTIP FOR HOVER */}
        <div className="neo-tooltip">
          <div><b>Sponsor ID:</b> {node?.sponsorPkId || "—"}</div>
          <div><b>Sponsor Name:</b> {node?.sponsorName || "—"}</div>
          <div><b>Joining Date:</b> {node?.joiningDate ? new Date(node.joiningDate).toLocaleDateString('en-GB') : "—"}</div>
        </div>
      </button>

      {hasChildren ? (
        <ul className="neo-ul">
          <NeoSlot child={left} side="LEFT" selectedId={selectedId} onSelect={onSelect} logoSrc={logoSrc} />
          <NeoSlot child={right} side="RIGHT" selectedId={selectedId} onSelect={onSelect} logoSrc={logoSrc} />
        </ul>
      ) : null}
    </li>
  );
}

function NeoSlot({ child, side, selectedId, onSelect, logoSrc }) {
  if (!child) {
    return (
      <li className="neo-li">
        <div className="neo-card empty card-bg-red" role="presentation">
          <div className="neo-name">Empty</div>
          <div className="neo-side">{side}</div>
          <div className="neo-id">—</div>
          <div className="neo-chip type-red">—</div>
        </div>
      </li>
    );
  }
  return <NeoNode node={child} selectedId={selectedId} onSelect={onSelect} logoSrc={logoSrc} />;
}

/** CSS */
const CSS =
  ".neo-page{min-height:100vh;background:#ffffff;color:#0f172a;font-family:ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto,Arial;}" +
  ".neo-error{margin:10px 12px 0;border-radius:12px;border:1px solid rgba(239,68,68,.25);background:rgba(239,68,68,.06);padding:10px 12px;color:#991b1b;font-size:13px;}" +

  ".neo-top{position:sticky;top:0;z-index:10;background:#006600;backdrop-filter:blur(10px);border-bottom:1px solid rgba(15,23,42,.10);padding:10px 10px 8px;color:#ffffff;}" +
  ".neo-zoom{display:flex;align-items:center;gap:6px;flex-wrap:wrap;justify-content:center;}" +
  ".neo-ic{width:32px;height:32px;border-radius:10px;border:1px solid rgba(15,23,42,.12);background:rgba(255,255,255,.10);color:#ffffff;font-weight:400;cursor:pointer;font-size:16px;line-height:1;transition:background .14s;}" +
  ".neo-ic:hover{background:rgba(255,255,255,.25);}" +
  ".neo-ic.ghost{opacity:.9;font-size:12px;font-weight:400;}" +
  ".neo-zoomv{font-size:11px;font-weight:400;opacity:.9;width:52px;text-align:center;color:#ffffff;}" +

  /* Depth selector */
  ".neo-depth-wrap{display:flex;align-items:center;gap:4px;padding:0 4px;border-radius:10px;border:1px solid rgba(15,23,42,.10);background:rgba(255,255,255,.05);}" +
  ".neo-depth-label{font-size:11px;font-weight:700;color:rgba(255,255,255,.80);padding:0 4px;white-space:nowrap;}" +
  ".neo-depth-btn{width:26px;height:26px;border-radius:7px;border:none;background:transparent;font-size:12px;font-weight:700;color:rgba(255,255,255,.70);cursor:pointer;transition:background .13s,color .13s;}" +
  ".neo-depth-btn:hover{background:rgba(255,255,255,.15);color:#ffffff;}" +
  ".neo-depth-btn.active{background:#f39c12;color:#ffffff;}" +
  ".neo-depth-btn:disabled{opacity:.5;cursor:not-allowed;}" +

  ".neo-btn:disabled{opacity:.7;cursor:not-allowed;}" +
  ".neo-custom-depth{display:flex;align-items:center;gap:4px;padding-left:8px;border-left:1px solid rgba(255,255,255,0.2);margin-left:4px;}" +
  ".neo-depth-input{width:50px;height:28px;border-radius:8px;border:1.5px solid rgba(255,255,255,0.2);background:rgba(255,255,255,0.1);color:#fff;text-align:center;font-weight:700;font-size:13px;outline:none;}" +
  ".neo-depth-input:focus{border-color:#f39c12;background:rgba(255,255,255,0.2);}" +
  ".neo-go-btn{height:28px;padding:0 12px;border-radius:8px;border:none;background:#f39c12;color:#fff;font-weight:800;font-size:12px;cursor:pointer;transition:all .14s;}" +
  ".neo-go-btn:hover{background:#e67e22;transform:scale(1.05);}" +
  ".neo-go-btn:disabled{opacity:0.5;cursor:not-allowed;}" +

  /* API pill */
  ".neo-api-pill{display:flex;align-items:center;justify-content:center;gap:6px;margin-top:5px;}" +
  ".neo-api-dot{width:7px;height:7px;border-radius:50%;background:#22c55e;flex-shrink:0;box-shadow:0 0 0 2px rgba(34,197,94,.20);}" +
  ".neo-api-text{font-size:11px;color:rgba(255,255,255,.60);font-family:ui-monospace,monospace;}" +
  ".neo-api-text b{color:#38bdf8;}" +

  /* Loading */
  ".neo-loading{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;padding:60px 20px;}" +
  ".neo-spinner{width:36px;height:36px;border-radius:50%;border:3px solid rgba(15,23,42,.15);border-top-color:#006600;animation:neo-spin .7s linear infinite;}" +
  "@keyframes neo-spin{to{transform:rotate(360deg)}}" +
  ".neo-loading-text{font-size:13px;font-weight:600;color:#64748b;}" +

  /* canvas + stage */
  ".neo-canvas{padding:10px 8px 100px;overflow:auto;-webkit-overflow-scrolling:touch;height:calc(100vh - 148px);display:block;background:#ffffff;}" +
  ".neo-stage{transform-origin:top center;display:inline-flex;flex-direction:column;align-items:center;min-width:100%;padding-bottom:100px;}" +

  /* tree */
  ".neo-tree{padding-left:0;margin:0;display:inline-block;position:relative;width:max-content;padding-bottom:10px;}" +
  ".neo-tree,.neo-tree ul{padding-top:14px;position:relative;}" +
  ".neo-ul{padding-left:0;margin:0 auto;display:flex;justify-content:center;gap:24px;width:100%;}" +
  ".neo-li{list-style:none;position:relative;padding:12px 6px 0 6px;display:flex;flex-direction:column;align-items:center;}" +

  /* lines */
  ".neo-li:before,.neo-li:after{content:'';position:absolute;top:0;right:50%;border-top:2px solid rgba(15,23,42,.10);width:50%;height:14px;}" +
  ".neo-li:after{right:auto;left:50%;border-left:2px solid rgba(15,23,42,.10);}" +
  ".neo-li:only-child:before,.neo-li:only-child:after{display:none;}" +
  ".neo-li:only-child{padding-top:0;}" +
  ".neo-li:first-child:before,.neo-li:last-child:after{border:0 none;}" +
  ".neo-li:last-child:before{border-right:2px solid rgba(15,23,42,.10);border-radius:0 10px 0 0;}" +
  ".neo-li:first-child:after{border-radius:10px 0 0 0;}" +
  ".neo-tree ul:before{content:'';position:absolute;top:0;left:50%;border-left:2px solid rgba(15,23,42,.10);width:0;height:14px;}" +

  /* card */
  ".neo-card{position:relative;width:132px;min-height:112px;border-radius:14px;padding:10px;border:1px solid rgba(15,23,42,0.1);background:#ffffff;box-shadow:0 14px 34px rgba(0,0,0,.08);display:flex;flex-direction:column;align-items:center;gap:6px;cursor:pointer;user-select:none;transition:transform .14s ease,box-shadow .14s ease,border-color .14s ease;}" +
  ".neo-card:hover{transform:translateY(-2px);border-color:rgba(14,165,233,0.3);z-index:20;}" +
  ".neo-card.sel{border-color:#f39c12;box-shadow:0 16px 46px rgba(243,156,18,0.2);}" +
  ".neo-card.empty{opacity:.55;cursor:default;background:rgba(15,23,42,0.05) !important;}" +
  /* Background colors — refined professional palette */
  ".card-bg-ent{background:#2F7D32!important;border-color:rgba(14,165,233,.4);}" +
  ".card-bg-ent:hover{box-shadow:0 16px 36px #2F7D32;}" +
  ".card-bg-red{background:#FD0100 !important;border-color:rgba(249,115,22,.4);}" +
  ".card-bg-red:hover{box-shadow:0 16px 36px #FD0100;}" +

  ".neo-name{font-size:13px;font-weight:950;letter-spacing:.2px;color:#ffffff;text-transform:lowercase;line-height:1.1;text-align:center;}" +
  ".neo-logo{width:36px;height:36px;object-fit:contain;border-radius:10px;border:1px solid #004d00;background:#006600;padding:5px;}" +
  ".neo-id{font-size:12px;font-weight:600;color:#38bdf8;line-height:1.1;}" +
  ".card-bg-ent .neo-id{color:#ffffff; opacity:0.9;}" +
  ".card-bg-red .neo-id{color:#ffffff; opacity:0.9;}" +
  ".neo-chip{font-size:10px;font-weight:600;padding:4px 8px;border-radius:999px;border:1px solid rgba(255,255,255,.20);background:rgba(255,255,255,.1);color:#ffffff;line-height:1.2;max-width:110px;width:100%;text-align:center;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;}" +
  ".neo-chip.type-ent{color:#ffffff;border-color:rgba(255,255,255,.3);background:rgba(34,197,94,0.3);}" +
  ".neo-chip.type-red{color:#ffffff;border-color:rgba(255,255,255,.3);background:rgba(239,68,68,0.3);}" +
  ".neo-side{font-size:11px;font-weight:900;color:#475569;}" +
  ".card-bg-red .neo-side{color:#ffffff;}" +

  /* tooltip */
  ".neo-tooltip{position:absolute;top:50%;left:100%;transform:translateY(-50%);margin-left:8px;background:#0f172a;color:#ffffff;padding:8px 12px;border-radius:8px;font-size:11px;font-weight:600;white-space:nowrap;opacity:0;visibility:hidden;transition:opacity .2s,visibility .2s;z-index:99;text-align:left;line-height:1.5;box-shadow:0 10px 15px -3px rgba(0,0,0,0.3);}" +
  ".neo-tooltip::after{content:'';position:absolute;top:50%;right:100%;transform:translateY(-50%);border-width:5px;border-style:solid;border-color:transparent #0f172a transparent transparent;}" +
  ".neo-card:hover:not(.empty) .neo-tooltip{opacity:1;visibility:visible;}" +

  ".neo-empty{margin:16px auto;width:fit-content;padding:10px 12px;border-radius:12px;border:1px dashed rgba(15,23,42,.20);color:#475569;background:transparent;font-size:13px;}" +
  ".neo-footer{padding:10px 10px 12px;text-align:center;color:rgba(255,255,255,.70);border-top:1px solid rgba(15,23,42,.10);background:#006600;font-size:12px;}" +

  /* mobile */
  "@media (max-width:520px){" +
  ".neo-canvas{height:calc(100vh - 170px);}" +
  ".neo-card{width:118px;min-height:106px;border-radius:13px;padding:9px;}" +
  ".neo-ul{gap:12px;}" +
  ".neo-logo{width:32px;height:32px;}" +
  ".neo-name{font-size:11px;}" +
  ".neo-id{font-size:11px;}" +
  ".neo-chip{font-size:10px;padding:4px 8px;}" +
  ".neo-depth-btn{width:22px;height:22px;font-size:11px;}" +
  "}";